// ReceiptCard - Display a single receipt with view and edit actions
// Shows receipt details with edit/delete/view buttons based on permissions

import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useToast } from '../../contexts/ToastContext';
import { receiptsAPI } from '../../services/api';
import type { Receipt } from '../../types';
import dayjs from 'dayjs';
import EditReceiptDialog from './EditReceiptDialog';
import ViewReceiptDialog from './ViewReceiptDialog';

interface ReceiptCardProps {
  receipt: Receipt;
  submissionId: string;
  programCategories: string[];  // ADDED: Dynamic categories
  canEdit: boolean;
  onDeleted: () => void;
  onUpdated: (receipt: Receipt) => void;
}

const ReceiptCard: React.FC<ReceiptCardProps> = ({
  receipt,
  submissionId,
  programCategories,  // ADDED: Receive categories
  canEdit,
  onDeleted,
  onUpdated,
}) => {
  const { showSuccess, showError } = useToast();
  const [deleting, setDeleting] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this receipt?')) {
      return;
    }

    try {
      setDeleting(true);
      await receiptsAPI.deleteReceipt(submissionId, receipt.receiptId);
      showSuccess('Receipt deleted successfully');
      onDeleted();
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to delete receipt');
    } finally {
      setDeleting(false);
    }
  };

  const handleReceiptUpdated = (updatedReceipt: Receipt) => {
    setEditDialogOpen(false);
    onUpdated(updatedReceipt);
  };

  return (
    <>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="start">
            <Box flex={1}>
              <Typography variant="h6" component="div" gutterBottom>
                {receipt.vendor}
              </Typography>

              <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                <Chip label={receipt.expenseCategory} size="small" variant="outlined" />
                <Chip
                  label={dayjs(receipt.receiptDate).format('MMM D, YYYY')}
                  size="small"
                  variant="outlined"
                />
              </Box>

              <Typography variant="h5" color="primary.main" gutterBottom>
                ${receipt.amount.toFixed(2)}
              </Typography>

              {receipt.notes && (
                <Typography variant="body2" color="text.secondary">
                  {receipt.notes}
                </Typography>
              )}
            </Box>
            
            {/* Action Buttons */}
            <Box display="flex" gap={0.5}>
              <Tooltip title="View Receipt">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => setViewDialogOpen(true)}
                >
                  <ViewIcon />
                </IconButton>
              </Tooltip>

              {canEdit && (
                <>
                  <Tooltip title="Edit Receipt">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => setEditDialogOpen(true)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Delete Receipt">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={handleDelete}
                      disabled={deleting}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Edit Dialog - UPDATED with programCategories */}
      <EditReceiptDialog
        open={editDialogOpen}
        submissionId={submissionId}
        receipt={receipt}
        programCategories={programCategories}  
        onClose={() => setEditDialogOpen(false)}
        onReceiptUpdated={handleReceiptUpdated}
      />

      {/* View Dialog */}
      <ViewReceiptDialog
        open={viewDialogOpen}
        receipt={receipt}
        onClose={() => setViewDialogOpen(false)}
      />
    </>
  );
};

export default ReceiptCard;